import classNames from 'classnames'
import { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { FormInput, useFormField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Switch } from 'core-components/library/checkbox'
import {
  selectIsQbrDocumentFinalized,
  selectQBRDocumentGlobalPartnerId,
  selectQBRDocumentName,
  selectQBRIsGlobalPlans,
} from 'partner360/features/QBR'
import { selectELTVersion } from 'partner360/features/QBR/selectors/selectELTVersion'
import { selectExportSlides } from 'partner360/features/QBR/selectors/selectExportSlides'

import { TRANSLATION_KEYS } from '../../../../../../../translations'

import { CurrencySettings } from './components/currencySettings/CurrencySettings'
import { Offerings } from './components/offerings/Offerings'
import { PeriodSelector } from './components/periodSelector/PeriodSelector'
import { QBR_SETTINGS_FORM_KEY } from './configs'
import { selectQbrSettingsForm } from './selectors/selectQbrSettingsForm'

import styles from './QbrSettingsForm.module.scss'

export const QbrSettingsForm: FC = () => {
  const documentName = useSelector(selectQBRDocumentName)
  const form = useSelector(selectQbrSettingsForm)
  const isFinalized = useSelector(selectIsQbrDocumentFinalized)
  const globalPartnerId = useSelector(selectQBRDocumentGlobalPartnerId)
  const isGlobalPlans = useSelector(selectQBRIsGlobalPlans)
  const eltVersion = useSelector(selectELTVersion)
  const exportSlides = useSelector(selectExportSlides)

  useFormField({
    formKey: QBR_SETTINGS_FORM_KEY,
    field: 'eltVersion',
    defaultValue: eltVersion,
  })

  useFormField({
    formKey: QBR_SETTINGS_FORM_KEY,
    field: 'exportSlides',
    defaultValue: exportSlides,
  })

  const { value: selectedIsGlobalPlans, handleChange: onChangeIsGlobalPlans } = useFormField<
    1 | 0 | null
  >({
    defaultValue: isGlobalPlans ?? null,
    field: 'isGlobalPlans',
    formKey: QBR_SETTINGS_FORM_KEY,
  })
  const isNameInputEmpty = useMemo(() => {
    if (!form) {
      return false
    }

    return form.formData.name.length === 0
  }, [form])

  return (
    <div className={styles.root}>
      <div>
        <PeriodSelector />
        <div className={styles.title}>
          <Translation text={TRANSLATION_KEYS.settings.name} />
          <div className={styles.required}> *</div>
        </div>
        <FormInput
          formKey={QBR_SETTINGS_FORM_KEY}
          field="name"
          defaultValue={documentName}
          margin={'md'}
          isDisabled={isFinalized}
          className={classNames({
            [styles.nameEmpty]: isNameInputEmpty,
            [styles.name]: !isNameInputEmpty,
          })}
        />
        <RenderWhen condition={Boolean(globalPartnerId)}>
          <Switch
            checked={selectedIsGlobalPlans === 0}
            onChange={(newValue: boolean | undefined) => {
              onChangeIsGlobalPlans(newValue ? 0 : 1)
            }}
            label={<Translation text={TRANSLATION_KEYS.settings.isGlobalPlans} />}
          />
        </RenderWhen>
      </div>

      <div>
        <div className={styles.title}>
          <Translation text={TRANSLATION_KEYS.settings.titleOfferingsSettings} />
          <div className={styles.required}> *</div>
        </div>
        <div className={styles.offeringsAlert}>
          <Translation text={TRANSLATION_KEYS.settings.offeringsWarning} />
        </div>
        <Offerings isDisabled={isFinalized} />
      </div>

      <CurrencySettings />
    </div>
  )
}
